import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import API from "../services/api";
import { toast } from "react-toastify";
import {
  User,
  IdCard,
  Hash,
  Lock,
  UserPlus,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle,
  GraduationCap,
} from "lucide-react";

const Register = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fullName: "",
    birthCertificateNo: "",
    admissionNo: "",
    password: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const calculateStrength = () => {
    const pwd = formData.password;
    let score = 0;
    if (pwd.length > 6) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    return score;
  };

  const strength = calculateStrength();
  const strengthColors = ["bg-red-500", "bg-orange-500", "bg-yellow-400", "bg-green-500"];
  const strengthLabels = ["Weak", "Fair", "Good", "Strong"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    try {
      setLoading(true);
      await API.post("/auth/register", {
        fullName: formData.fullName,
        birthCertificateNo: formData.birthCertificateNo,
        admissionNo: formData.admissionNo,
        password: formData.password,
        role: "STUDENT",
      });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      toast.error(err.response?.data?.message || "Registration failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (name) =>
    `flex items-center border-2 rounded-xl px-3 transition-all duration-200 ${
      focusedField === name
        ? "border-blue-700 bg-blue-50/40 shadow-sm"
        : "border-gray-200 bg-white hover:border-gray-300"
    }`;

  const iconClass = (name) =>
    `mr-2 flex-shrink-0 transition-colors duration-200 ${
      focusedField === name ? "text-blue-700" : "text-gray-400"
    }`;

  // ── Success screen ──────────────────────────────────────
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div
          className="bg-white shadow-2xl rounded-2xl p-12 text-center"
          style={{ animation: "popIn 0.5s ease both" }}
        >
          <div
            className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ animation: "popIn 0.5s ease 0.15s both", opacity: 0 }}
          >
            <CheckCircle size={44} className="text-green-500" />
          </div>
          <h2 className="text-2xl font-bold mb-2 text-gray-800">Account Created!</h2>
          <p className="text-gray-500 text-sm">Redirecting you to login...</p>
          <div className="mt-6 w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
            <div className="h-full bg-green-500 rounded-full" style={{ animation: "progressBar 2s linear both" }} />
          </div>
        </div>
        <style>{`
          @keyframes popIn {
            0%   { opacity: 0; transform: scale(0.7); }
            70%  { transform: scale(1.05); }
            100% { opacity: 1; transform: scale(1); }
          }
          @keyframes progressBar {
            from { width: 0%; }
            to   { width: 100%; }
          }
        `}</style>
      </div>
    );
  }

  const fields = [
    { label: "Full Name",               name: "fullName",         icon: <User size={18} />,   type: "text",     placeholder: "Enter your full name"     },
    { label: "Birth Certificate Number",name: "birthCertificateNo",icon: <IdCard size={18} />, type: "text",     placeholder: "Enter certificate number" },
    { label: "Admission Number",        name: "admissionNo",      icon: <Hash size={18} />,   type: "text",     placeholder: "Enter admission number"   },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12 relative overflow-hidden">

      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-100 rounded-full opacity-40 animate-pulse" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-200 rounded-full opacity-30 animate-pulse" style={{ animationDelay: "1.5s" }} />
      </div>

      <div
        className="relative bg-white shadow-xl rounded-2xl w-full max-w-md p-8"
        style={{ animation: "slideUp 0.5s ease both" }}
      >
        {/* Header */}
        <div className="text-center mb-7" style={{ animation: "slideUp 0.5s ease 0.05s both", opacity: 0 }}>
          <div className="w-16 h-16 bg-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
            style={{ animation: "bounceIn 0.6s ease both" }}>
            <GraduationCap size={30} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-primary">Student Registration</h1>
          <p className="text-sm text-gray-500 mt-1">
            Create an account to apply for constituency bursary assistance.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">

          {/* Regular fields */}
          {fields.map((field, i) => (
            <div
              key={field.name}
              style={{ animation: `slideUp 0.5s ease ${0.1 + i * 0.07}s both`, opacity: 0 }}
            >
              <label className="text-sm font-medium text-gray-700 block mb-1.5">
                {field.label}
              </label>
              <div className={inputClass(field.name)}>
                <span className={iconClass(field.name)}>{field.icon}</span>
                <input
                  name={field.name}
                  type={field.type}
                  onChange={handleChange}
                  onFocus={() => setFocusedField(field.name)}
                  onBlur={() => setFocusedField(null)}
                  className="w-full py-2.5 outline-none bg-transparent text-sm"
                  placeholder={field.placeholder}
                  required
                />
              </div>
            </div>
          ))}

          {/* Password */}
          <div style={{ animation: "slideUp 0.5s ease 0.31s both", opacity: 0 }}>
            <label className="text-sm font-medium text-gray-700 block mb-1.5">Password</label>
            <div className={`${inputClass("password")} relative`}>
              <span className={iconClass("password")}><Lock size={18} /></span>
              <input
                name="password"
                type={showPassword ? "text" : "password"}
                onChange={handleChange}
                onFocus={() => setFocusedField("password")}
                onBlur={() => setFocusedField(null)}
                className="w-full py-2.5 outline-none bg-transparent text-sm pr-8"
                placeholder="Create password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {/* Strength meter */}
            {formData.password && (
              <div className="mt-2">
                <div className="flex gap-1">
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                        i < strength ? strengthColors[strength - 1] : "bg-gray-200"
                      }`}
                    />
                  ))}
                </div>
                {strength > 0 && (
                  <p className="text-xs mt-1 text-gray-500">
                    Password strength:{" "}
                    <span className={`font-medium ${
                      strength < 2 ? "text-red-500" : strength < 4 ? "text-yellow-600" : "text-green-600"
                    }`}>
                      {strengthLabels[strength - 1]}
                    </span>
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div style={{ animation: "slideUp 0.5s ease 0.38s both", opacity: 0 }}>
            <label className="text-sm font-medium text-gray-700 block mb-1.5">Confirm Password</label>
            <div className={inputClass("confirm")}>
              <span className={iconClass("confirm")}><Lock size={18} /></span>
              <input
                name="confirmPassword"
                type={showPassword ? "text" : "password"}
                onChange={handleChange}
                onFocus={() => setFocusedField("confirm")}
                onBlur={() => setFocusedField(null)}
                className="w-full py-2.5 outline-none bg-transparent text-sm"
                placeholder="Confirm password"
                required
              />
              {formData.confirmPassword && (
                <span className="ml-2 flex-shrink-0">
                  {formData.password === formData.confirmPassword ? (
                    <CheckCircle size={16} className="text-green-500" />
                  ) : (
                    <span className="w-4 h-4 rounded-full border-2 border-red-400 flex items-center justify-center text-red-400 text-xs font-bold">✕</span>
                  )}
                </span>
              )}
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-blue-800 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed mt-2"
            style={{ animation: "slideUp 0.5s ease 0.44s both", opacity: 0 }}
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Creating Account...
              </>
            ) : (
              <>
                <UserPlus size={18} />
                Create Account
              </>
            )}
          </button>
        </form>

        <div className="my-6 flex items-center gap-3">
          <div className="flex-1 border-t border-gray-200" />
          <span className="text-xs text-gray-400">OR</span>
          <div className="flex-1 border-t border-gray-200" />
        </div>

        <p
          className="text-sm text-center text-gray-600"
          style={{ animation: "slideUp 0.5s ease 0.5s both", opacity: 0 }}
        >
          Already have an account?{" "}
          <Link to="/login" className="text-primary font-semibold hover:underline">
            Login here
          </Link>
        </p>
      </div>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(22px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes bounceIn {
          0%   { opacity: 0; transform: scale(0.5); }
          70%  { transform: scale(1.1); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default Register;